/**
 * The engagement beacon — batched, bounded, and never in the learner's way.
 *
 * Sibling of `progressWriter.ts`, and deliberately NOT the same shape, because
 * the two carry different kinds of fact:
 *
 *  - A progress write is a SNAPSHOT. The server merges monotonically, so an
 *    older one carries nothing a newer one does not, and dropping the middle of
 *    a burst is the whole design.
 *  - An event is an OBSERVATION. Each one is a separate row and a separate
 *    thing that happened, so they queue rather than coalesce — and a queue that
 *    can grow needs a ceiling, which a snapshot slot never did.
 *
 * THE RULES, each of which is a way the stream could quietly lie:
 *
 *  1. **One ORDINARY request in flight.** Overlapping beacons would let a retry
 *     and its original both land, and while ingest is idempotent on
 *     `(session, clientEventId)`, two concurrent inserts of the same id race
 *     the constraint rather than deduplicating politely. A TEARDOWN drain is
 *     the exception and overtakes: its batches are disjoint from the one
 *     already in the air, so it is not the case the rule exists for — and
 *     waiting meant the last thing a visit ever emitted was queued behind a
 *     request the closing document was about to cancel.
 *  2. **Teardown empties the queue, not one batch of it — including the batch
 *     already in the air.** An outage can leave ten batches waiting; sending
 *     the first and disposing stranded the other nine. And an ORDINARY request
 *     outstanding when the document goes away is cancelled with it, which is
 *     the batch a closing tab most wants: hiding flushes, `pagehide` follows
 *     milliseconds later, and the interval banked on the way out was in the
 *     request the browser then killed. The drain re-sends it with the same
 *     ids, which ingest recognises. All of it goes at once — there is no time
 *     to send it in turn — bounded by the browser's page-wide keepalive
 *     allowance, which is a BYTE budget and the only limit here that is not a
 *     count.
 *  3. **A failed batch goes back to the FRONT, with its ids intact.** That is
 *     what the idempotency key is for: the same event re-sent is recognised
 *     and counted once. Re-assigning ids on retry would double someone's time
 *     on site, which IS the lead score.
 *  4. **A REFUSED batch is not a failed one.** A network error is worth
 *     retrying; a 4xx is the server saying this payload will never be
 *     accepted, and re-sending it forever parks it at the head of the queue
 *     where it blocks every later observation behind it. Refused batches are
 *     dropped and counted.
 *  5. **The ceiling is enforced in ONE place.** Telemetry must never grow
 *     without bound in a tab left open on a flaky connection, and the oldest
 *     goes first: the recent picture is the one worth keeping. Both the arrival
 *     path and the retry path go through `#trim`, because when they did it
 *     separately the retry path dropped the NEWEST events and counted none of
 *     them.
 *  6. **Nothing here throws into the app.** A lecture must play when the
 *     telemetry endpoint is down. Every send failure is swallowed after being
 *     counted.
 */
import type { EngagementEventInput, EventIngestBody } from '@cusp/contracts';
import { MAX_EVENT_BATCH } from '@cusp/core';

export interface BeaconSendOptions {
  /** The document is going away; the request must outlive it. */
  keepalive: boolean;
}

export interface BeaconOptions {
  sessionKey: string;
  send: (body: EventIngestBody, options: BeaconSendOptions) => Promise<unknown>;
  /**
   * Whether a send failure means "this batch will never be accepted" rather
   * than "try again". Defaults to treating everything as retryable, which is
   * the safe direction for a caller that has not thought about it; the
   * provider supplies the real predicate, because knowing an `ApiError`'s
   * status is the transport's business and not the queue's.
   */
  isPermanent?: (error: unknown) => boolean;
  /** How often a non-empty queue is flushed. */
  intervalMs?: number;
  /**
   * The ceiling on unsent events. Ten batches' worth: enough to ride out a
   * genuine outage, small enough that a tab open overnight on a dead network
   * cannot accumulate a megabyte of JSON.
   */
  maxQueue?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void;
}

export const BEACON_INTERVAL_MS = 15_000;
const DEFAULT_MAX_QUEUE = MAX_EVENT_BATCH * 10;

/**
 * How many bytes one teardown may hand to `keepalive` requests.
 *
 * Browsers cap the TOTAL keepalive body a page has outstanding at 64KB and
 * reject the request that crosses it — so the batch limit being a count is not
 * enough here, and a queue that built up through an outage is exactly the case
 * where the difference shows. The headroom below 64KB is for the request
 * envelope and for the sibling `progressWriter`, which is spending the same
 * allowance from the same `pagehide`.
 */
export const KEEPALIVE_BUDGET_BYTES = 56_000;

/** UTF-8 bytes, not UTF-16 units: the budget is the browser's, in its terms. */
const encoder = new TextEncoder();
function byteLength(json: string): number {
  return encoder.encode(json).length;
}

export class EventBeacon {
  #queue: EngagementEventInput[] = [];
  /** The batch an ordinary request is carrying, so a teardown can rescue it. */
  #airborne: EngagementEventInput[] | null = null;
  /** Set when a teardown has taken that batch over; its failure is then moot. */
  #rescued = false;
  /** What every request costs before a single event is in it. */
  readonly #envelope: number;
  #inFlight = false;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #disposed = false;
  #dropped = 0;
  #refused = 0;

  readonly #opts: Required<Omit<BeaconOptions, 'send' | 'sessionKey'>> &
    Pick<BeaconOptions, 'send' | 'sessionKey'>;

  constructor(options: BeaconOptions) {
    this.#opts = {
      intervalMs: options.intervalMs ?? BEACON_INTERVAL_MS,
      maxQueue: options.maxQueue ?? DEFAULT_MAX_QUEUE,
      isPermanent: options.isPermanent ?? (() => false),
      now: options.now ?? (() => Date.now()),
      setTimer: options.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
      clearTimer: options.clearTimer ?? ((h) => clearTimeout(h)),
      send: options.send,
      sessionKey: options.sessionKey,
    };
    this.#envelope = byteLength(JSON.stringify({ sessionKey: options.sessionKey, events: [] }));
  }

  /** How many events have been dropped to the ceiling. Visible, never silent. */
  get dropped(): number {
    return this.#dropped;
  }

  /** How many the server refused outright. A different gap, counted apart. */
  get refused(): number {
    return this.#refused;
  }

  get pending(): number {
    return this.#queue.length;
  }

  /** Queue one observation. Never throws, never blocks. */
  record(event: EngagementEventInput): void {
    if (this.#disposed) return;
    /*
     * SERIALISED HERE, OR NOT QUEUED AT ALL. `metadata` is
     * `Record<string, unknown>` on the wire, so a caller can hand this a cycle
     * or a BigInt — and the first thing to touch it would otherwise be
     * `JSON.stringify`, inside a `pagehide` handler or a React cleanup, where
     * a throw is a broken lecture rather than a lost event.
     *
     * A COPY, not a check, because the queue holds an event for fifteen
     * seconds and the object it was handed belongs to somebody else for all of
     * them. An observation is what was true when it happened; a caller who
     * mutates their own object afterwards must not be able to change it, and
     * certainly must not be able to make it unserialisable after it has been
     * accepted. Here the only thing at stake is the one observation that could
     * never have been sent anyway.
     */
    let snapshot: EngagementEventInput;
    try {
      snapshot = JSON.parse(JSON.stringify(event)) as EngagementEventInput;
    } catch {
      this.#dropped += 1;
      return;
    }
    this.#queue.push(snapshot);
    this.#trim();
    // A full batch goes now rather than waiting out the interval — the cap is
    // the server's, so holding more than one batch back buys nothing.
    if (this.#queue.length >= MAX_EVENT_BATCH) void this.#flush();
    else this.#arm();
  }

  /**
   * Send whatever is queued.
   *
   * `keepalive` is for teardown: an ordinary request started from `pagehide`
   * is cancelled with the page that started it, so the whole queue goes at
   * once rather than a batch at a time.
   */
  flush(keepalive = false): void {
    if (keepalive) this.#drain();
    else void this.#flush();
  }

  /**
   * Stop new work. A request already in the air is left to land.
   *
   * Anything still queued is COUNTED as it goes: a disposed beacon has no
   * timer to re-arm and nothing left holding a reference to it, so those
   * events are never going to be sent, and a gap this file did not admit to is
   * the one thing every rule in it is written against.
   */
  dispose(): void {
    this.#disposed = true;
    this.#disarm();
    this.#dropped += this.#queue.length;
    this.#queue = [];
  }

  /**
   * The ceiling, in one place. Oldest first: the recent picture is the one
   * worth keeping, and a reader who sees a gap at the start of a visit can
   * tell it happened.
   */
  #trim(): void {
    const over = this.#queue.length - this.#opts.maxQueue;
    if (over <= 0) return;
    this.#dropped += over;
    this.#queue = this.#queue.slice(over);
  }

  #arm(): void {
    if (this.#timer !== null || this.#disposed) return;
    this.#timer = this.#opts.setTimer(() => {
      this.#timer = null;
      void this.#flush();
    }, this.#opts.intervalMs);
  }

  #disarm(): void {
    if (this.#timer === null) return;
    this.#opts.clearTimer(this.#timer);
    this.#timer = null;
  }

  /**
   * As much of the queue as the browser will carry, all at once.
   *
   * Sequentially would be the tidier shape and is not available: there is no
   * `await` between `pagehide` and the document going away, so every batch has
   * to be in the air before this function returns. Anything the budget cannot
   * cover stays queued — visible in `pending` — because a teardown is not
   * always a farewell: StrictMode's replay and a membership switch both come
   * through here with the tab still very much alive.
   */
  #drain(): void {
    let budget = KEEPALIVE_BUDGET_BYTES;
    /*
     * The batch already in the air goes FIRST and goes again. Its request was
     * started without `keepalive`, so the closing document cancels it — and it
     * holds the oldest events, which on the ordinary path to a closed tab
     * (hide, flush, `pagehide` a moment later) is the interval that was just
     * banked. Sending it twice is what the idempotency key is for; the ids are
     * the ones it was given when it happened.
     *
     * ONCE, though. That path is three teardowns, not one — hide, `pagehide`,
     * and the cleanup behind them — and rescuing on each would put the same
     * batch in the air three times over, which is precisely the pile-up rule 1
     * exists to prevent.
     */
    const airborne = this.#rescued ? null : this.#airborne;
    const rescue = airborne === null ? 0 : this.#envelope + sizeOf(airborne);
    if (airborne !== null && rescue <= budget) {
      this.#rescued = true;
      budget -= rescue;
      void this.#deliver(airborne, true);
    }
    while (this.#queue.length > 0 && budget > 0) {
      const batch = this.#take(budget);
      budget -= this.#envelope + sizeOf(batch);
      void this.#deliver(batch, true);
    }
    if (this.#queue.length > 0) this.#arm();
  }

  /** One ordinary batch, with at most one of them in the air (rule 1). */
  async #flush(): Promise<void> {
    if (this.#inFlight || this.#queue.length === 0) return;
    const batch = this.#take();
    this.#inFlight = true;
    this.#airborne = batch;
    this.#rescued = false;
    this.#disarm();
    try {
      await this.#deliver(batch, false, () => this.#rescued);
    } finally {
      this.#inFlight = false;
      this.#airborne = null;
    }
    // More waiting, including anything queued while the request was out.
    if (this.#queue.length > 0) this.#arm();
  }

  /**
   * The next batch off the front, capped by the server's own limit and — for a
   * teardown — by what is left of the browser's keepalive allowance. One event
   * always goes even if it is bigger than the budget: an oversized event is
   * the server's to refuse, not this queue's to sit on forever.
   */
  #take(budgetBytes = Number.POSITIVE_INFINITY): EngagementEventInput[] {
    let size = this.#envelope;
    let count = 0;
    while (count < MAX_EVENT_BATCH && count < this.#queue.length) {
      const next = this.#queue[count];
      if (next === undefined) break;
      const bytes = sizeOf(next);
      if (count > 0 && size + bytes > budgetBytes) break;
      size += bytes;
      count += 1;
    }
    const batch = this.#queue.slice(0, count);
    this.#queue = this.#queue.slice(count);
    return batch;
  }

  async #deliver(
    batch: EngagementEventInput[],
    keepalive: boolean,
    rescued?: () => boolean,
  ): Promise<void> {
    try {
      await this.#opts.send({ sessionKey: this.#opts.sessionKey, events: batch }, { keepalive });
    } catch (error) {
      // A teardown has already re-sent this batch with a credential that
      // outlives the page; putting it back would only send it a third time.
      if (rescued?.() === true) return;
      if (this.#opts.isPermanent(error)) {
        // Rule 4. Re-sending a batch the server has already refused would park
        // it at the head of the queue and block every later observation.
        this.#refused += batch.length;
      } else if (this.#disposed) {
        // The beacon this batch belonged to has been retired; there is nowhere
        // to put it back. Counted rather than dropped in silence.
        this.#dropped += batch.length;
      } else {
        // BACK TO THE FRONT, IDS INTACT. Ingest is idempotent on
        // `(session, clientEventId)`, so re-sending the same events is counted
        // once — which is only true because they keep the ids they were given
        // when they happened.
        this.#queue = [...batch, ...this.#queue];
        this.#trim();
      }
    }
  }
}

/** How much wire this event, or this batch of them, is worth. */
function sizeOf(value: EngagementEventInput | EngagementEventInput[]): number {
  return byteLength(JSON.stringify(value));
}
